import React, { useEffect, useRef } from "react";
import { Table, Typography } from "@optimizely/axiom";
import { datadogRum } from "@datadog/browser-rum";
import { useUser } from "../../../../hooks/useUser/useUser";

export const PRINCIPAL_USERS_EMPTY_LABEL = "No principal user assigned";

/**
 * Lists the principal users who own a virtual teammate. The list endpoint omits
 * `principalUsers`, so the detail record is fetched by email to resolve them.
 *
 * When any referenced id cannot be resolved the API drops the field entirely and logs the
 * unresolved id to Application Insights, so a deleted owner reaches us as an absent field and
 * falls through to the empty state rather than rendering a "NotFound" row.
 */
export const UserFormPrincipalUsers = ({ email }: { email: string }) => {
    const { user, error, isLoading } = useUser({ email });
    const principalUsers = user?.principalUsers ?? [];

    // SWR retries failed requests with exponential back-off, and every attempt throws a new
    // Error instance. Without this guard the effect re-runs on each retry and reports the same
    // failure to Datadog repeatedly. Keyed on email so a different user still gets reported.
    const loggedEmailRef = useRef<string | null>(null);

    useEffect(() => {
        // Clear on recovery so a later, distinct failure is reported rather than deduped
        // against the previous one.
        if (!error) {
            loggedEmailRef.current = null;
            return;
        }

        if (loggedEmailRef.current === email) return;

        loggedEmailRef.current = email;
        datadogRum.addError(new Error("Unable to resolve principalUsers for virtual teammate"), {
            email,
            error,
            component: "UserFormPrincipalUsers"
        });
    }, [email, error]);

    return (
        <div data-testid="principal-users-field" data-cy="principal-users-field">
            {isLoading ? (
                <Typography type="body" tag="div" className="label--disabled">
                    Loading...
                </Typography>
            ) : principalUsers.length ? (
                <Table
                    density="loose"
                    // eslint-disable-next-line react/style-prop-object
                    style="rule-no-bottom-border"
                >
                    <Table.THead>
                        <Table.TR>
                            <Table.TH sorting={{}}>Name</Table.TH>
                            <Table.TH sorting={{}}>Email</Table.TH>
                        </Table.TR>
                    </Table.THead>
                    <Table.TBody>
                        {principalUsers.map((principalUser) => (
                            <Table.TR key={principalUser.userId}>
                                <Table.TD>
                                    {`${principalUser.firstName ?? ""} ${principalUser.lastName ?? ""}`.trim()}
                                </Table.TD>
                                <Table.TD>{principalUser.email}</Table.TD>
                            </Table.TR>
                        ))}
                    </Table.TBody>
                </Table>
            ) : (
                <Typography type="body" tag="div">
                    {PRINCIPAL_USERS_EMPTY_LABEL}
                </Typography>
            )}
        </div>
    );
};

UserFormPrincipalUsers.displayName = "UserFormPrincipalUsers";
